import { useCallback } from 'react'
import { useArcVoiceCall } from '../../contexts/ArcVoiceCallContext'
import {
  arcPresence,
  ArcPresenceState,
  describeWrite,
  IArcElsewhereNote,
  IArcPresenceEntry,
} from '../../helpers/arcPresence/arcPresenceStore'
import { isOnAgenda } from '../../helpers/arcVoice/callAgenda'
import { useArcElsewhere, useArcOffScreenWrites } from '../../hooks/useArcPresence'
import ArcVoiceWorkingItems from '../ArcVoice/ArcVoiceWorkingItems'
import ArcActivityCard, { ArcActivityTone } from './ArcActivityCard'
import styles from './ArcPresence.module.scss'

const DoneNote = ({ note }: { note: IArcElsewhereNote }) => {
  const handleDismiss = useCallback(() => arcPresence.dismissElsewhere(note.id), [note.id])
  return (
    <ArcActivityCard
      icon="check-circle"
      tone={ArcActivityTone.Done}
      title={note.text}
      link={note.link}
      onDismiss={handleDismiss}
    />
  )
}

const InFlightWrite = ({ entry }: { entry: IArcPresenceEntry }) => {
  const title = describeWrite(entry.service, entry.label, false)
  return entry.state === ArcPresenceState.NeedsApproval ? (
    <ArcActivityCard icon="check-shield" title={title} status="Needs your OK in the chat" />
  ) : (
    <ArcActivityCard icon="loader" tone={ArcActivityTone.Working} title={title} />
  )
}

/** Whether an Arc turn is one the call already shows on its agenda. */
const useIsCallTurn = (): ((turnId: string) => boolean) => {
  const { snapshot } = useArcVoiceCall()
  const agenda = snapshot?.agenda
  return useCallback((turnId: string) => !!agenda && isOnAgenda(agenda, turnId), [agenda])
}

/**
 * Everything Arc is doing where you aren't looking, down the left edge at one width:
 * a call's agenda, the changes it just made, and the ones it is still making — from
 * a call, a chat, or anywhere else Arc runs.
 */
const ArcActivityRail = () => {
  const done = useArcElsewhere()
  const inFlight = useArcOffScreenWrites(useIsCallTurn())
  return (
    <ul className={styles.rail} aria-label="What Arc is doing" aria-live="polite">
      <ArcVoiceWorkingItems />
      {done.map((note) => (
        <DoneNote key={note.id} note={note} />
      ))}
      {inFlight.map((entry) => (
        <InFlightWrite key={entry.id} entry={entry} />
      ))}
    </ul>
  )
}

export default ArcActivityRail
